import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, InputAdornment, Stack, TextField } from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "./Api";
import { hasAuthRole, setAuthSession } from "./auth";
import LoginShell from "./LoginShell";
import PasswordResetDialog from "./PasswordResetDialog";

const LoginAdmin = () => {
  const [adminId, setAdminId] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasAuthRole("admin")) navigate("/admin");
  }, [navigate]);

  const handleAdminLogin = async () => {
    if (!adminId.trim() || !adminPassword) {
      toast.error("Enter admin ID and password");
      return;
    }
    if (otpRequired && !otp.trim()) {
      toast.error("Enter Telegram OTP");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/api/auth/login", {
        username: adminId.trim(),
        password: adminPassword,
        role: "admin",
        otp: otpRequired ? otp.trim() : undefined,
      });
      if (res.data?.otp_required) {
        setOtpRequired(true);
        setOtp("");
        toast.success(res.data.message || "OTP sent on Telegram");
        return;
      }
      setAuthSession(res.data);
      toast.success("Admin login successful");
      setTimeout(() => {
        window.location.href = "/admin";
      }, 600);
    } catch (error) {
      console.error("Admin login failed:", error);
      toast.error(error.response?.data?.error || "Invalid Admin Credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginShell
      eyebrow="Admin access"
      title="Admin / Super Admin"
      subtitle="Sign in with your SCAN-APP admin account."
      footer={
        <Button
          fullWidth
          variant="text"
          onClick={() => setResetOpen(true)}
          sx={{
            color: "#006a71",
            fontWeight: 800,
            textTransform: "none",
            justifyContent: "center",
          }}
        >
          Forgot password?
        </Button>
      }
    >
      <Stack spacing={2}>
      <TextField
        label="Admin ID"
        value={adminId}
        onChange={(e) => {
          setAdminId(e.target.value);
          setOtpRequired(false);
          setOtp("");
        }}
        fullWidth
        autoComplete="username"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Password"
        type="password"
        value={adminPassword}
        onChange={(e) => {
          setAdminPassword(e.target.value);
          setOtpRequired(false);
          setOtp("");
        }}
        onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
        fullWidth
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {otpRequired && (
        <TextField
          label="Telegram OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
          fullWidth
          autoComplete="one-time-code"
          helperText="OTP sent to the master Telegram account."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      <Button
        variant="contained"
        onClick={handleAdminLogin}
        disabled={loading}
        fullWidth
        size="large"
        startIcon={loading ? <AdminPanelSettingsRoundedIcon /> : <LoginRoundedIcon />}
        sx={{
          py: 1.25,
          fontWeight: 900,
          textTransform: "none",
          background: "linear-gradient(90deg, #006a71, #1976d2)",
          boxShadow: "0 14px 28px rgba(25, 118, 210, 0.28)",
          "&:hover": {
            background: "linear-gradient(90deg, #00545a, #1565c0)",
          },
        }}
      >
        {loading ? (otpRequired ? "Verifying..." : "Sending OTP...") : (otpRequired ? "Verify OTP" : "Login")}
      </Button>
      </Stack>
      <PasswordResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        initialUsername={adminId}
      />
    </LoginShell>
  );
};

export default LoginAdmin;
